import React, { useContext } from 'react';
import styled from 'styled-components';
import { StringParam, useQueryParam } from 'use-query-params';
import {
  allMetricDescriptions,
  getAllowedGoals,
  isProfitMetricAvailable,
  isReturnsMetricAvailable,
  MetricKind,
} from '@/webapi/models';
import { Legend } from '@/features/details/charts/chart-legend';
import { formatEnum } from '@/utils/types';
import { VSpace } from '@/components/spacing';
import {
  ElementTracking,
  Experience,
  ExperienceTag,
  useExperienceApi,
} from '@/webapi/use-experience-api';
import { Tooltip } from '@/components/tooltip';
import { Chart } from '@/features/details/charts/chart';
import { SummaryContext } from '@/features/details/summary/context';
import { AssistedProducts } from '@/features/details/charts/assisted-products';
import { Card } from '@/components/card';
import { notProfitMetric, notReturnsMetric } from '@/features/details/utils';
import { TopProducts } from '@/features/details/charts/assisted-products/top-products';
import {
  SessionsWithImpressionsTooltipText,
  UsersTooltipText,
} from '@/features/details/sessions-type-chip';
import { breakpoints, isMobile } from '@/components/responsive';
import { isPriceTestGoal } from '@/features/details/price-test-reports/shared';
import { clone } from '@/features/editor/widgets/shared/modals/audience/facebook-audience/ad-search/shared';
import { getCustomExpEventName } from '@/features/details/summary/see-more-reports-list';
import { SummaryResponse } from '@/webapi/use-analytics-api';

type Props = {
  experience: Experience;
  variations: string[];
  loading: boolean;
  data: any[];
  setExperience: (e: Experience) => void;
};

export function Charts({
  data,
  loading,
  experience,
  variations,
  setExperience,
}: Props) {
  const goals = getChartsGoals(experience);
  const { setExperienceGoals, loading: debounce } = useExperienceApi(
    experience.lastDevicePreview,
    experience,
  );

  const isAudience = experience?.isHidden;
  const { data: summaryData } = useContext(SummaryContext);
  const [sessionsType] = useQueryParam(`st`, StringParam);
  const isDividedByUsers = sessionsType === `users`;

  const isMobileDevice = isMobile();

  const showAssistedProducts = shouldShowAssistedProducts(
    summaryData,
    experience,
  );
  const charts: JSX.Element[] = goals?.map((goal, idx) => {
    const isPrimaryGoal = idx === 0;
    const hideExperience = async () => {
      if (!isPrimaryGoal && !debounce) {
        const cloned = clone(experience) as Experience;
        cloned.goals = cloned.goals.filter((metric) => metric !== goal);
        await setExperienceGoals(experience.primaryGoal, cloned.goals);
        setExperience(cloned);
      }
    };

    return (
      <div key={`${goal}${idx}`}>
        {showAssistedProducts && idx === 1 && (
          <AssistedProducts summaryData={summaryData} isAudience={isAudience} />
        )}
        <VSpace key={`${goal}${idx}space1`} value={2} />
        <DetailsCard key={`${goal}${idx}DetailsCard`}>
          <Wrapper key={`${goal}${idx}Wrapper`}>
            <Title>
              <Row>
                {formatTitle(
                  goal,
                  idx,
                  sessionsType,
                  !!isAudience,
                  experience?.elementTracking,
                )}
                {!isMobileDevice && (
                  <Tooltip
                    title={formatTooltipTitle(goal, idx, sessionsType)}
                    text={getDescription(goal, isDividedByUsers, sessionsType)}
                  />
                )}
              </Row>
              {shouldShowChartLegend(experience, isAudience) && (
                <Legend key={`${goal}${idx}legend`} variations={variations} />
              )}
              <Hide hidden={isPrimaryGoal} onClick={hideExperience}>
                Hide
              </Hide>
            </Title>
            <VSpace key={`${goal}${idx}space2`} className="only-desktop" />
            <Chart goal={goal} data={data} loading={loading} />
          </Wrapper>
        </DetailsCard>
      </div>
    );
  });

  if (goals?.length === 1 && showAssistedProducts) {
    charts.push(
      <AssistedProducts summaryData={summaryData} isAudience={isAudience} />,
    );
  }
  return (
    <div style={{ width: `100%` }}>
      {charts}
      <TopProducts summaryData={summaryData} isAudience={isAudience} />
    </div>
  );
}

const DetailsCard = styled(Card)`
  && {
    cursor: default;
    padding: 3.5rem;
    border-radius: 2rem;
    justify-content: center;
    align-items: center;

    ${breakpoints.down(`md`)} {
      width: 90%;
      margin: 0 auto;
      padding: 2rem;
      border-radius: 0.8rem;
    }
  }
`;

const Hide = styled.button`
  display: flex;
  z-index: 999;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: #f6f7f8;
  color: #656e72;
  border-radius: 25px;
  padding: 0.9rem 1rem;
  font-family: Inter, serif;
  font-size: 1.2rem;
  font-weight: 600;
  opacity: ${(p: { hidden: boolean }) => (p.hidden ? `0` : `1`)};

  :hover {
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.09), 0 1px 4px 0 rgba(7, 6, 6, 0.04);
    opacity: ${(p: { hidden: boolean }) => (p.hidden ? `0` : `0.5`)};
    cursor: ${(p: { hidden: boolean }) => (p.hidden ? `default` : `pointer`)};
  }

  :active {
    opacity: ${(p: { hidden: boolean }) => (p.hidden ? `0` : `1`)};
    cursor: default;
  }

  ${breakpoints.down(`md`)} {
    display: none;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
`;

const Title = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0;
  font-family: Inter, serif;
  color: black;
  font-weight: 800;
  font-size: 1.8rem;

  ${breakpoints.down(`md`)} {
    font-size: 1.4rem;
    flex-direction: column;
    gap: 1.5rem;
    align-items: flex-start;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 5rem 1fr;
  grid-gap: 2rem;

  ${breakpoints.down(`md`)} {
    display: flex;
    flex-direction: column;
  }
`;

export function formatTitle(
  goal: MetricKind,
  idx: number,
  sessionsType: string,
  isAudience?: boolean,
  elementTracking: ElementTracking[] = [],
) {
  const { getCustomMetricName } = useContext(SummaryContext);
  if (getCustomMetricName?.(goal)) {
    return getCustomMetricName?.(goal);
  }
  if (getCustomExpEventName(goal, elementTracking)) {
    return getCustomExpEventName(goal, elementTracking);
  }
  let title = formatEnum(goal)
    .toLowerCase()
    .split(` `)
    .map((s) => `${s[0].toUpperCase()}${s.substring(1, s.length)}`)
    .join(` `);
  if (goal === MetricKind.SESSIONS && sessionsType === `users`) {
    title = `Users`;
  }
  return `${title}${idx === 0 && !isAudience ? ` (Primary Goal)` : ``}`;
}

function getChartsGoals(experience: Experience) {
  const primaryGoal = experience?.primaryGoal;
  const goals = experience?.goals?.filter((g) => g !== primaryGoal);
  if (primaryGoal) {
    goals.unshift(primaryGoal);
  }
  let goalList = getAllowedGoals(goals, experience.tag);
  if (!isProfitMetricAvailable(experience.metricsStartAt)) {
    goalList = goalList.filter(notProfitMetric());
  }
  if (!isReturnsMetricAvailable(experience.metricsStartAt)) {
    goalList = goalList.filter(notReturnsMetric());
  }
  return goalList.filter((g) => !isPriceTestGoal(g));
}

function formatTooltipTitle(
  goal: MetricKind,
  idx: number,
  sessionsType: string,
) {
  return formatTitle(goal, idx, sessionsType).replace(`(Primary Goal)`, ``);
}

function getDescription(
  goal: MetricKind,
  isDividedByUsers: boolean,
  sessionsType: string,
): string {
  if (goal === MetricKind.SESSIONS && sessionsType === `users`) {
    return UsersTooltipText;
  }
  if (goal === MetricKind.SESSIONS && sessionsType === `sessions`) {
    return SessionsWithImpressionsTooltipText;
  }
  const metricText = allMetricDescriptions.get(goal);
  if (isDividedByUsers && goal !== MetricKind.AVG_SESSION_DURATION) {
    return metricText
      ?.replaceAll(`session`, `user`)
      ?.replaceAll(`sessions`, `users`);
  }
  return metricText;
}

const shouldShowAssistedProducts = (
  summaryData: SummaryResponse,
  experience: Experience,
) =>
  summaryData?.topProducts?.length > 0 ||
  summaryData?.returns?.length > 0 ||
  (experience?.tag === ExperienceTag.RECS &&
    summaryData?.assistedProducts?.length > 0);

const shouldShowChartLegend = (experience: Experience, isAudience: boolean) =>
  !experience.isPostPurchase && !isAudience;
